// THE ACTIVITY DROPDOWN (owner 2026-11-05: "why there is no activity dropdown, that
// automatically highlights, systems inside areas, and areas inside levels and levels
// inside the project. makes easy complex full selections").
//
// Pick one or more schedule activities and the levels, areas and systems they name are put
// in the scope. This is not a guess: an activity carries `level`, `areaId` and `system`
// (BA03 has 1,580 of 1,620 with a level, 1,148 with an area and 660 with a system, and they
// are named "L1 · Area A · Conduits"), so the hierarchy being lit up is the schedule's own.
//
// Two ways to use it, because both are wanted: Add leaves what is already picked alone and
// adds, Only replaces the scope with exactly what these activities cover.
//
// A level is matched by the KEY the SERVER uses (isoModel.levelKeyOf = elementAreas.levelKey),
// never by the string and never by just stripping punctuation: the same floor is written
// "Level 1", "L1" and "Level1" across BA03's schedule, and all three are L1.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Select, Tooltip } from "antd";
import { listGanttItems } from "api/gantt/ganttApi";
import { levelKeyOf } from "./isoModel";

/** What a set of activities covers, as scope keys, against the structure that is on screen. */
export function coverageOf(activities, { structure = null, areas = {} } = {}) {
  const levelKeys = new Set();
  const areaIds = new Set();
  const systems = new Set();
  const liveLevels = new Set(((structure && structure.levels) || []).map((l) => l.key));
  // an activity's areaId is a REVISION of an area; the sign-off works in area sets
  const setOfRevision = new Map();
  for (const [id, a] of Object.entries(areas || {})) {
    setOfRevision.set(String(id), String(id));
    for (const rev of (a.revisionIds || [])) setOfRevision.set(String(rev), String(id));
  }
  for (const a of activities || []) {
    const k = levelKeyOf(a.level);
    if (k && liveLevels.has(k)) levelKeys.add(k);
    const areaId = a.areaSetId || a.areaId;
    if (areaId) {
      const set = setOfRevision.get(String(areaId));
      if (set) areaIds.add(set);
    }
    if (a.system) systems.add(String(a.system));
  }
  return { levelKeys: [...levelKeys], areaIds: [...areaIds], systems: [...systems] };
}

export default function ActivityScopeSelect({ projectId, structure, areas, onPick, onReplace }) {
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!projectId) { setItems([]); return undefined; }
    setLoading(true);
    Promise.resolve().then(() => listGanttItems(projectId))
      .then((res) => { if (alive) setItems(Array.isArray(res) ? res : (res && res.items) || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  // only the activities that actually name a place - the rest cannot light anything up
  const options = useMemo(() => (items || [])
    .filter((a) => a && !a.disabled && (a.level || a.areaId || a.system))
    .map((a) => ({
      value: String(a._id),
      label: a.name || [a.level, a.system].filter(Boolean).join(" · ") || "Activity",
      where: [a.level, a.system].filter(Boolean).join(" · "),
    }))
    .slice(0, 4000), [items]);
  const byId = useMemo(() => new Map((items || []).map((a) => [String(a._id), a])), [items]);
  const chosen = useMemo(() => picked.map((id) => byId.get(id)).filter(Boolean), [picked, byId]);
  const cover = useMemo(() => coverageOf(chosen, { structure, areas }), [chosen, structure, areas]);
  const summary = chosen.length
    ? `${cover.levelKeys.length} ${cover.levelKeys.length === 1 ? "level" : "levels"}, ${cover.areaIds.length} ${cover.areaIds.length === 1 ? "area" : "areas"}, ${cover.systems.length} ${cover.systems.length === 1 ? "system" : "systems"}`
    : "";

  return (
    <div className="signoff-acts-pick" data-testid="activity-scope">
      <Tooltip title="Pick schedule activities and the levels, areas and systems they name are put in the scope. An activity carries its own level, area and system.">
        <Select mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                loading={loading} value={picked} onChange={setPicked} maxTagCount="responsive"
                placeholder={loading ? "Reading the schedule…" : `From an activity (${options.length})`}
                options={options} data-testid="activity-select" />
      </Tooltip>
      {chosen.length ? (
        <div className="d-flex align-items-center mt-1" style={{ gap: 6 }}>
          <small className="signoff-muted" data-testid="activity-cover">{summary}</small>
          <span className="models-spacer" style={{ flex: 1 }} />
          <Tooltip title="Add what these activities cover to what is already picked">
            <Button size="small" onClick={() => onPick(cover)} data-testid="activity-add">Add</Button>
          </Tooltip>
          <Tooltip title="Replace the scope with exactly what these activities cover">
            <Button size="small" onClick={() => onReplace(cover)} data-testid="activity-only">Only</Button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}
